/**
 * Phantom Wallet Button
 * Uses Solana Wallet Adapter for consistent state across all components
 * Shows USDT, TAKARA and LAIKA balances on Solana
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useWallet } from '@solana/wallet-adapter-react'
import { useQuery } from '@tanstack/react-query'
import { Wallet, ChevronDown, LogOut, Copy, Check } from 'lucide-react'
// solanaService import removed - balance fetching no longer needed
import { api } from '../../services/api'
import { toast } from 'sonner'
import { isMobileDevice, isAndroidDevice, isPhantomReady, safePhantomCall, waitForPhantomReady } from '../../utils/device'

// Debug helper for wallet connection issues
const debugPhantom = () => {
  // @ts-ignore - Phantom injects this at runtime
  const phantom = window.phantom?.solana
  console.log('=== Phantom Debug ===')
  // @ts-ignore
  console.log('window.phantom exists:', !!window.phantom)
  console.log('window.phantom.solana exists:', !!phantom)
  console.log('isPhantom:', phantom?.isPhantom)
  console.log('isConnected:', phantom?.isConnected)
  console.log('publicKey:', phantom?.publicKey?.toString())
  console.log('===================')
  return phantom
}

// Helper to get address string from various key formats
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getAddressString = (key: any): string => {
  if (!key) return ''
  if (typeof key === 'string') return key
  if (typeof key.toBase58 === 'function') return key.toBase58()
  if (typeof key.toString === 'function') return key.toString()
  return String(key)
}

interface PhantomButtonProps {
  variant?: 'primary' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export function PhantomButton({
  variant = 'primary',
  size = 'md',
  className = '',
}: PhantomButtonProps) {
  const { t } = useTranslation(['wallet', 'common'])
  const { publicKey, connected, connecting, connect, disconnect, select, wallets } = useWallet()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  
  // Get current user to check wallet ownership
  const { data: userResponse } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => api.getCurrentUser(),
    enabled: api.isAuthenticated(),
    retry: false,
  })
  const user = userResponse?.data
  
  // Check direct Phantom connection (in case adapter isn't synced)
  const [phantomDirectConnected, setPhantomDirectConnected] = useState(false)
  const [phantomDirectPublicKey, setPhantomDirectPublicKey] = useState<string | null>(null)
  
  // Monitor direct Phantom connection state
  useEffect(() => {
    const checkPhantom = () => {
      // @ts-ignore
      const phantom = window.phantom?.solana
      const disconnectFlag = localStorage.getItem('wallet_disconnected')
      
      if (phantom?.isPhantom) {
        // If disconnect flag is set, disconnect from Phantom
        if (disconnectFlag === 'true' && phantom.isConnected) {
          safePhantomCall(() => phantom.disconnect())
          setPhantomDirectConnected(false)
          setPhantomDirectPublicKey(null)
        } else {
        const isConnected = phantom.isConnected || false
        const pubKey = phantom.publicKey?.toString() || null
        setPhantomDirectConnected(isConnected)
        setPhantomDirectPublicKey(pubKey)
        }
      } else {
        setPhantomDirectConnected(false)
        setPhantomDirectPublicKey(null)
      }
    }
    
    checkPhantom()
    const interval = setInterval(checkPhantom, 1000)
    return () => clearInterval(interval)
  }, [])
  
  // Use adapter state if available, otherwise fall back to direct Phantom state
  const isActuallyConnected = connected || phantomDirectConnected
  const actualPublicKey = publicKey || (phantomDirectPublicKey ? { toBase58: () => phantomDirectPublicKey } : null)

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Balance fetching removed - no longer needed
  // Transaction verification on backend ensures user has sufficient funds

  // Auto-connect if Phantom is already connected but adapter isn't synced
  useEffect(() => {
    // Check disconnect flag FIRST, before any other logic
    const disconnectFlag = localStorage.getItem('wallet_disconnected')

    // Don't auto-connect if user explicitly disconnected - check this FIRST
    if (disconnectFlag === 'true') {
      // Also disconnect from Phantom directly if it's connected
      if (isPhantomReady()) {
        // @ts-ignore
        const phantom = window.phantom?.solana
        safePhantomCall(
          () => phantom.disconnect(),
          undefined,
          '[PhantomButton] Disconnect after flag check'
        )
      }

      return
    }

    // Skip if already connected via adapter
    if (connected) {
      return
    }

    // IMPORTANT: Skip auto-connect on mobile (Phantom mobile app handles this differently)
    if (isMobileDevice()) {
      console.log('[PhantomButton] Skipping auto-connect on mobile device')
      return
    }

    // IMPORTANT: Skip auto-connect if referral code is present (let user register first)
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('ref')) {
      console.log('[PhantomButton] Skipping auto-connect - referral flow active')
      return
    }

    const checkAndSyncPhantom = async () => {
      // Double-check disconnect flag before syncing
      const disconnectFlagCheck = localStorage.getItem('wallet_disconnected')
      if (disconnectFlagCheck === 'true') {
        return
      }

      // Check if Phantom is ready to avoid "Method not found" errors
      if (!isPhantomReady()) {
        console.log('[PhantomButton] Phantom not ready, skipping auto-connect')
        return
      }

      // @ts-ignore
      const phantom = window.phantom?.solana

      // If Phantom is connected but adapter isn't, sync them
      if (phantom?.isPhantom && phantom?.isConnected && phantom?.publicKey) {
        try {
          const phantomWallet = wallets.find(w => w.adapter.name === 'Phantom')
          if (phantomWallet) {
            // Select and connect via adapter
            select(phantomWallet.adapter.name)
            await new Promise(resolve => setTimeout(resolve, 300))

            // Try silent connect
            try {
              await connect()
            } catch (err) {
              // Don't try direct connect here as it might cause issues
              console.warn('[PhantomButton] Silent connect failed:', err)
            }
          }
        } catch (error) {
          // Error syncing
          console.warn('[PhantomButton] Sync error:', error)
        }
      }
    }

    // Check after a short delay to avoid race conditions
    const timeoutId = setTimeout(checkAndSyncPhantom, 500)

    // Also listen for Phantom connection events
    // @ts-ignore
    const phantom = window.phantom?.solana
    let cleanup: (() => void) | undefined

    // Only set up event listeners if Phantom is ready
    if (phantom && isPhantomReady()) {
      const handleConnect = () => {
        // Don't sync if user explicitly disconnected
        const disconnectFlagEvent = localStorage.getItem('wallet_disconnected')

        if (disconnectFlagEvent === 'true') {
          // Disconnect from Phantom immediately
          safePhantomCall(
            () => phantom.disconnect(),
            undefined,
            '[PhantomButton] Disconnect after connect event'
          )
          return
        }
        checkAndSyncPhantom()
      }

      // Wrap event listener registration with try-catch
      try {
        phantom.on('connect', handleConnect)
        cleanup = () => {
          try {
            phantom.off('connect', handleConnect)
          } catch (err) {
            console.warn('[PhantomButton] Error removing event listener:', err)
          }
        }
      } catch (err) {
        console.warn('[PhantomButton] Error adding event listener:', err)
      }
    }

    return () => {
      clearTimeout(timeoutId)
      cleanup?.()
    }
  }, [connected, select, connect, wallets])

  // Save wallet to backend when connected (use actual public key)
  useEffect(() => {
    const disconnectFlag = localStorage.getItem('wallet_disconnected')
    if (disconnectFlag === 'true') {
      // If disconnect flag is set, ensure wallet is disconnected
      if (connected || (phantomDirectPublicKey && phantomDirectConnected)) {
        disconnect()
        try {
          // @ts-ignore
          const phantom = window.phantom?.solana
          if (phantom?.isPhantom && phantom?.isConnected) {
            safePhantomCall(() => phantom.disconnect())
          }
        } catch (err) {
          // Ignore errors
        }
      }
      return
    }

    const pubKey = publicKey || (phantomDirectPublicKey ? { toBase58: () => phantomDirectPublicKey } : null)
    if (!pubKey || !api.isAuthenticated()) return

    const pubKeyString = getAddressString(pubKey)

    // Check if wallet matches user's wallet
    if (user?.walletAddress && user.walletAddress !== pubKeyString) {
      // Wallet doesn't match user's account - disconnect it
      disconnect()
      try {
        // @ts-ignore
        const phantom = window.phantom?.solana
        if (phantom?.isPhantom && phantom?.isConnected) {
          phantom.disconnect()
        }
      } catch (err) {
        // Ignore errors
      }
      return
    }
    
    // Debounce to prevent rapid calls
    const timeoutId = setTimeout(() => {
      api.connectSolana(pubKeyString).catch((err: any) => {
        // If wallet is already assigned to another account, disconnect it
        if (err?.response?.data?.message?.includes('already') || 
            err?.response?.data?.message?.includes('another account')) {
          disconnect()
          try {
            // @ts-ignore
            const phantom = window.phantom?.solana
            if (phantom?.isPhantom && phantom?.isConnected) {
              safePhantomCall(() => phantom.disconnect())
            }
          } catch (disconnectErr) {
            // Ignore errors
          }
          toast.error('This wallet is already connected to another account')
        } else if (!err?.response?.data?.message?.includes('already connected')) {
      console.error('Failed to save wallet:', err)
        }
    })
    }, 500)
    
    return () => clearTimeout(timeoutId)
  }, [publicKey, phantomDirectPublicKey, user?.walletAddress, connected, phantomDirectConnected, disconnect])

  const handleConnect = useCallback(async () => {
    // Clear disconnect flag when user manually connects
    localStorage.removeItem('wallet_disconnected')
    try {
      // Debug: log Phantom state
      const phantom = debugPhantom()

      if (!phantom?.isPhantom) {
        toast.error('Phantom extension not detected')
        window.open('https://phantom.app/', '_blank')
        return
      }

      // Check if Phantom is connected (already trusted)
      if (phantom.isConnected && phantom.publicKey) {
        toast.success('Phantom already connected')
        return
      }

      // Try silent connect first (if previously trusted)
      try {
        const silentResponse = await phantom.connect({ onlyIfTrusted: true })
        if (silentResponse?.publicKey) {
          toast.success('Phantom connected')
          // Update adapter state
          const phantomWallet = wallets.find(w => w.adapter.name === 'Phantom')
          if (phantomWallet) {
            select(phantomWallet.adapter.name)
          }
          return
        }
      } catch {
        // Not previously trusted, will prompt user
        console.log('Phantom not pre-trusted, will prompt user')
      }

      // Find Phantom wallet in available wallets
      const phantomWallet = wallets.find(w => w.adapter.name === 'Phantom')
      if (!phantomWallet) {
        toast.error('Phantom wallet not found in adapter')
        // Try direct connect anyway
        const response = await phantom.connect()
        if (response?.publicKey) {
          toast.success('Phantom connected')
        }
        return
      }

      // Check adapter readiness
      if (phantomWallet.adapter.readyState !== 'Installed') {
        console.warn('Adapter not ready, trying direct connect')
        const response = await phantom.connect()
        if (response?.publicKey) {
          toast.success('Phantom connected')
          select(phantomWallet.adapter.name)
        }
        return
      }

      // Select Phantom wallet first
      select(phantomWallet.adapter.name)

      // Wait for adapter to be ready
      await new Promise(resolve => setTimeout(resolve, 300))

      // Try adapter connect
      try {
        await connect()
        toast.success('Phantom connected')
        return
      } catch (adapterError) {
        console.warn('Adapter connect failed:', adapterError)
        // Fallback: Connect directly via native Phantom API
        const response = await phantom.connect()
        if (response?.publicKey) {
          toast.success('Phantom connected')
        } else {
          throw new Error('No public key returned')
        }
      }
    } catch (err: unknown) {
      console.error('Phantom connect error:', err)
      const error = err as { code?: number; message?: string; name?: string }

      if (error.message?.includes('timeout')) {
        toast.error('Connection timed out. Please try again.')
      } else if (error.name === 'WalletNotReadyError') {
        toast.error('Phantom not ready. Please refresh.')
      } else if (error.name === 'WalletConnectionError') {
        toast.error('Connection failed. Please try again.')
      } else if (error.code === 4001 || error.message?.includes('User rejected')) {
        toast.error('Connection rejected by user')
      } else if (error.message?.includes('Unexpected error')) {
        // This is a Phantom internal error
        toast.error('Phantom error. Try: 1) Unlock wallet 2) Refresh page 3) Restart browser')
      } else {
        toast.error('Connection failed - please refresh')
      }
    }
  }, [connect, select, wallets])

  const handleDisconnect = useCallback(async () => {
    try {
      // Set disconnect flag first to prevent auto-reconnect
      localStorage.setItem('wallet_disconnected', 'true')
      
      // Disconnect from adapter
      await disconnect()

      // Also disconnect from Phantom directly if possible
      if (isPhantomReady()) {
        // @ts-ignore
        const phantom = window.phantom?.solana
        await safePhantomCall(
          () => phantom.disconnect(),
          undefined,
          '[PhantomButton] Failed to disconnect Phantom'
        )
      }

      toast.info('Phantom disconnected')
    } catch (err) {
      // Already disconnected, but still set the flag
      localStorage.setItem('wallet_disconnected', 'true')
    }
  }, [disconnect])

  const handleClick = () => {
    if (connected) {
      setIsMenuOpen(!isMenuOpen)
    } else {
      handleConnect()
    }
  }

  const handleCopyAddress = () => {
    if (actualPublicKey) {
      const address = getAddressString(actualPublicKey)
      navigator.clipboard.writeText(address)
      setCopied(true)
      toast.success('Address copied!')
      setTimeout(() => setCopied(false), 2000)
    }
  }

  // Balance refresh removed - no longer needed

  const formatAddress = (addr: string) => `${addr.slice(0, 4)}...${addr.slice(-4)}`

  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2.5 text-base',
    lg: 'px-6 py-3 text-lg',
  }

  const variantStyles = {
    primary: isActuallyConnected ? 'bg-purple-700 text-white' : 'bg-purple-600 text-white',
    outline: 'border-2 border-purple-500 text-purple-400 hover:bg-purple-500/10',
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={handleClick}
        disabled={connecting}
        className={`
          flex items-center justify-center gap-2 rounded-lg font-semibold transition-all
          hover:opacity-90
          ${variantStyles[variant]}
          ${sizeStyles[size]}
          ${connecting ? 'opacity-50 cursor-not-allowed' : ''}
          ${className}
        `}
      >
        <Wallet className="h-4 w-4" />
        {connecting ? (
          <span>Connecting...</span>
        ) : isActuallyConnected && actualPublicKey ? (
          <span className="flex items-center gap-2">
            <span>{formatAddress(getAddressString(actualPublicKey))}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
          </span>
        ) : (
          <span>Phantom</span>
        )}
      </button>

      {/* Dropdown Menu */}
      {isMenuOpen && isActuallyConnected && actualPublicKey && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-background-card border border-green-900/30 rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Wallet Address */}
          <div className="p-4 border-b border-green-900/20">
            <div className="text-xs text-gray-400 mb-1">Wallet Address</div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-white font-mono">{formatAddress(getAddressString(actualPublicKey))}</span>
              <button
                onClick={handleCopyAddress}
                className="p-1 hover:bg-white/10 rounded transition-colors"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5 text-gray-400" />}
              </button>
            </div>
          </div>

          {/* Disconnect Button */}
          <div className="p-3 border-t border-green-900/20">
            <button
              onClick={() => {
                handleDisconnect()
                setIsMenuOpen(false)
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span>Disconnect</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Compact version for mobile
export function PhantomButtonCompact({ className = '' }: { className?: string }) {
  const { publicKey, connected, connecting, connect, disconnect, select, wallets } = useWallet()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Balance fetching removed - no longer needed

  // Get current user to check wallet ownership
  const { data: userResponse } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => api.getCurrentUser(),
    enabled: api.isAuthenticated(),
    retry: false,
  })
  const user = userResponse?.data

  useEffect(() => {
    const disconnectFlag = localStorage.getItem('wallet_disconnected')
    if (disconnectFlag === 'true') {
      if (connected) {
        disconnect()
        try {
          // @ts-ignore
          const phantom = window.phantom?.solana
          if (phantom?.isPhantom && phantom?.isConnected) {
            safePhantomCall(() => phantom.disconnect())
          }
        } catch (err) {
          // Ignore errors
        }
      }
      return
    }

    if (!publicKey || !api.isAuthenticated()) return

    const address = publicKey.toBase58()
    
    // Check if wallet matches user's wallet
    if (user?.walletAddress && user.walletAddress !== address) {
      // Wallet doesn't match user's account - disconnect it
      disconnect()
      try {
        // @ts-ignore
        const phantom = window.phantom?.solana
        if (phantom?.isPhantom && phantom?.isConnected) {
          phantom.disconnect()
        }
      } catch (err) {
        // Ignore errors
      }
      return
    }

    api.connectSolana(address).catch((err: any) => {
      // If wallet is already assigned to another account, disconnect it
      if (err?.response?.data?.message?.includes('already') || 
          err?.response?.data?.message?.includes('another account')) {
        disconnect()
        try {
          // @ts-ignore
          const phantom = window.phantom?.solana
          if (phantom?.isPhantom && phantom?.isConnected) {
            safePhantomCall(() => phantom.disconnect())
          }
        } catch (disconnectErr) {
          // Ignore errors
        }
      }
    })
  }, [publicKey, user?.walletAddress, connected, disconnect])

  const handleConnect = async () => {
    try {
      // @ts-ignore
      const phantom = window.phantom?.solana
      if (!phantom?.isPhantom) {
        toast.error('Phantom not detected')
        window.open('https://phantom.app/', '_blank')
        return
      }

      const phantomWallet = wallets.find(w => w.adapter.name === 'Phantom')
      if (!phantomWallet) {
        toast.error('Phantom not found')
        window.open('https://phantom.app/', '_blank')
        return
      }

      select(phantomWallet.adapter.name)
      await new Promise(resolve => setTimeout(resolve, 300))

      // Try adapter first
      try {
        await connect()
        toast.success('Connected')
        return
      } catch (adapterError) {
        console.warn('Adapter failed, trying native API:', adapterError)
      }

      // Fallback to native Phantom API
      const response = await phantom.connect()
      if (response?.publicKey) {
        toast.success('Connected')
      } else {
        throw new Error('Connection failed')
      }
    } catch (err: unknown) {
      const error = err as { code?: number; message?: string; name?: string }
      if (error.code === 4001 || error.message?.includes('User rejected')) {
        toast.error('Rejected')
      } else {
        toast.error('Failed - try refreshing')
      }
    }
  }

  const handleClick = () => {
    if (connected) {
      setIsMenuOpen(!isMenuOpen)
    } else {
      handleConnect()
    }
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={handleClick}
        disabled={connecting}
        className={`
          flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold
          ${connected ? 'bg-purple-700' : 'bg-purple-600'} text-white
          hover:opacity-90 transition-opacity
          ${connecting ? 'opacity-50 cursor-not-allowed' : ''}
          ${className}
        `}
      >
        <Wallet className="h-4 w-4" />
        {connecting ? (
          'Connecting...'
        ) : connected && publicKey ? (
          <span className="flex items-center gap-1.5">
            <span>{publicKey.toBase58().slice(0, 4)}...{publicKey.toBase58().slice(-4)}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
          </span>
        ) : (
          'Connect Phantom'
        )}
      </button>

      {/* Dropdown Menu */}
      {isMenuOpen && connected && publicKey && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-background-card border border-green-900/30 rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Disconnect */}
          <div className="p-3 border-t border-green-900/20">
            <button
              onClick={async () => {
                try {
                  await disconnect()
                  toast.info('Disconnected')
                } catch {}
                setIsMenuOpen(false)
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-sm transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span>Disconnect</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default PhantomButton
